import { BrandsModel, inventoryApi } from '@pukka/api';
import { assertUtil } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { proxy } from '@pukka/valtio';

import { BrandsState } from '../Brands.state';
import { BrandsAssignState } from '../Modal/BrandsAssign.state';

const INLINE_ERROR_CODE = ['0072-0002', '0072-0003', '0093-0001', '0093-0002', '0093-0003', '0093-0004'];
const GENERAL_ERROR_CODE = ['UNSPECIFIED', 'ERR_NETWORK', 'ERR_BAD_RESPONSE'];

type FormType = 'create' | 'edit';
type EntityType = 'brand' | 'parent';

type SelectedPrincipal = { id: number; name: string };

interface IBrandsFormState extends Record<string, unknown> {
  isOpen: boolean;
  isLoading: boolean;
  type: FormType | null;

  entityType: EntityType | null;
  editId: number | null;

  brandName: string;
  selectedPrincipal: SelectedPrincipal | null;
  brandItemCounts: number;
  brandItemNames: string[];

  parentName: string;
  brandList: { id: number; name: string }[];

  initialBrandData: BrandsModel.BrandDetail | null;
  initialParentData: BrandsModel.ParentDetail | null;

  showDeleteModal: boolean;

  showDeleteAllOutletModal: boolean;

  nameError: string;

  isSaveDisabled: boolean;

  handleClickCreateParent(): void;
  handleClickCreateEmptyBrand(): void;
  handleClickCreateBrand(principal?: SelectedPrincipal): void;
  handleClickEditParent(id: string | number, name: string): void;
  handleClickEditBrand(id: number, name: string, principal: SelectedPrincipal, itemCount: number): void;

  handleCloseForm(): void;
  handleChangeName(name: string, entityType: EntityType): void;
  handleClickDelete(): void;
  handleToggleDeleteModal(): void;
  handleToggleDeleteAllOutletModal(): void;

  handleMutationError(e: unknown): void;

  createParent(
    payload: BrandsModel.CreateParentRequest,
    handler?: (data: BrandsModel.CreateParentRequest) => Promise<BrandsModel.ParentDetail>,
  ): void;
  updateParent(
    principalId: number,
    payload: BrandsModel.UpdateParentRequest,
    handler?: (parentId: number, data: BrandsModel.UpdateParentRequest) => Promise<BrandsModel.ParentDetail>,
  ): void;
  deleteParent(
    parentId: number,
    outletId: number,
    isAllOutlet: boolean,
    parentName: string,
    handler?: (id: number, outletId: number, isAllOutlet: boolean) => void,
  ): void;

  createBrand(
    outletId: number,
    payload: BrandsModel.CreateBrandRequest,
    handler?: (outletId: number, data: BrandsModel.CreateBrandRequest) => Promise<BrandsModel.BrandDetail>,
  ): void;
  updateBrand(
    outletId: number,
    brandId: number,
    payload: BrandsModel.UpdateBrandRequest,
    handler?: (outletId: number, brandId: number, data: BrandsModel.UpdateBrandRequest) => Promise<BrandsModel.BrandDetail>,
  ): void;
  deleteBrand(
    brandId: number,
    outletId: number,
    isAllOutlet: boolean,
    handler?: (id: number, outletId: number, isAllOutlet: boolean) => void,
  ): void;

  handleClickAssign(): void;
  handleSubmitShortcutAssign(): void;

  handleClickSave(): void;
  handleClickSaveToAllOutlet(): void;
}

/* istanbul ignore next */
function handleGeneralError(e: unknown) {
  assertUtil.assertObjectIsTruthy<{
    code: string;
    message: string;
  }>(e);

  /* istanbul ignore next */
  if (GENERAL_ERROR_CODE.includes(e.code)) {
    toast.error('A technical error has occurred. Please try again.');
  } else {
    toast.error(e.message || 'A technical error has occurred. Please try again.');
  }
}

function isSameNumberArray(arr1: number[], arr2: number[]) {
  return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort());
}

function isSaveBrandDisabled(
  type: FormType | null,
  brandName: string,
  initialBrandData: BrandsModel.BrandDetail | null,
  selectedPrincipal: SelectedPrincipal | null,
) {
  const isEmpty = !brandName;

  if (type === 'create') {
    return isEmpty;
  }

  // for edit
  const isSameName = brandName === initialBrandData?.name;
  let principalId = selectedPrincipal?.id;

  if (principalId === -1) {
    principalId = null as unknown as number;
  }

  const isSamePrincipal = principalId === initialBrandData?.principal_id;

  return isEmpty || (isSameName && isSamePrincipal);
}

function isSaveParentDisabled(
  type: FormType | null,
  parentName: string,
  initialParentData: BrandsModel.ParentDetail | null,
  brandList: { id: number; name: string }[],
) {
  const isEmpty = !parentName;

  if (type === 'create') {
    return isEmpty;
  }

  // for edit
  const isSameName = parentName === initialParentData?.name;

  const initialIds = initialParentData?.brands.map((brand) => brand.id) || [];
  const currentIds = brandList.map((brand) => brand.id);
  const isSameBrand = isSameNumberArray(initialIds, currentIds);

  return isEmpty || (isSameName && isSameBrand);
}

async function getBrandDetail(id: number, outletId: number) {
  return inventoryApi.getBrandDetail(id, outletId);
}

async function parentCreatorHandler(data: BrandsModel.CreateParentRequest) {
  return inventoryApi.createParent(data);
}

async function brandCreatorHandler(outletId: number, data: BrandsModel.CreateBrandRequest) {
  return inventoryApi.createBrand(outletId, data);
}

async function parentUpdatorHandler(principalId: number, data: BrandsModel.UpdateParentRequest) {
  return inventoryApi.updateParent(principalId, data);
}

async function brandUpdatorHandler(outletId: number, brandId: number, data: BrandsModel.UpdateBrandRequest) {
  return inventoryApi.updateBrand(outletId, brandId, data);
}

async function parentDeletionHandler(id: number, outletId: number, isAllOutlet: boolean) {
  return inventoryApi.deleteParent(id, outletId, isAllOutlet);
}

async function brandDeletionHandler(id: number, outletId: number, isAllOutlet: boolean) {
  return inventoryApi.deleteBrand(id, outletId, isAllOutlet);
}

const BrandsFormState: IBrandsFormState = proxy({
  isOpen: false,
  isLoading: false,
  type: null,

  entityType: null,
  editId: null,

  // for brand
  brandName: '',
  selectedPrincipal: null,
  brandItemCounts: 0,
  brandItemNames: [],

  // for parent
  parentName: '',
  brandList: [],

  initialBrandData: null,
  initialParentData: null,

  showDeleteModal: false,
  showDeleteAllOutletModal: false,

  nameError: '',

  get isSaveDisabled() {
    // for brand
    if (BrandsFormState.entityType === 'brand') {
      return isSaveBrandDisabled(
        BrandsFormState.type,
        BrandsFormState.brandName,
        BrandsFormState.initialBrandData,
        BrandsFormState.selectedPrincipal,
      );
    }

    // for parent
    return isSaveParentDisabled(
      BrandsFormState.type,
      BrandsFormState.parentName,
      BrandsFormState.initialParentData,
      BrandsFormState.brandList,
    );
  },

  handleClickCreateEmptyBrand: () => {
    BrandsFormState.handleClickCreateBrand();
  },

  handleClickCreateBrand: (principal?: SelectedPrincipal) => {
    BrandsFormState.isOpen = true;
    BrandsFormState.type = 'create';
    BrandsFormState.entityType = 'brand';

    if (principal) {
      BrandsFormState.selectedPrincipal = principal;
    }
  },

  handleClickCreateParent: () => {
    BrandsFormState.isOpen = true;
    BrandsFormState.type = 'create';
    BrandsFormState.entityType = 'parent';
  },

  handleClickEditParent: async (id: number, name: string) => {
    try {
      const data = await inventoryApi.getParentDetail(id, BrandsState.selectedOutlet.key);

      BrandsFormState.isOpen = true;
      BrandsFormState.type = 'edit';
      BrandsFormState.entityType = 'parent';
      BrandsFormState.editId = id;
      BrandsFormState.parentName = name;
      BrandsFormState.brandList = data.brands;
      BrandsFormState.initialParentData = data;
    } catch (e) {
      // TODO: handle error
      /* istanbul ignore next */
      BrandsFormState.handleMutationError(e);
    }
  },

  handleClickEditBrand: async (id: number, name: string, principal: SelectedPrincipal) => {
    try {
      const data = await getBrandDetail(id, BrandsState.selectedOutlet.key);

      BrandsFormState.isOpen = true;
      BrandsFormState.type = 'edit';
      BrandsFormState.entityType = 'brand';
      BrandsFormState.editId = id;
      BrandsFormState.brandName = name;
      BrandsFormState.selectedPrincipal = principal;
      BrandsFormState.brandItemCounts = data.item_count;
      BrandsFormState.brandItemNames = data.item_names;
      BrandsFormState.initialBrandData = data;
    } catch (e) {
      // TODO: handle error
      /* istanbul ignore next */
      BrandsFormState.handleMutationError(e);
    }
  },

  handleCloseForm: () => {
    BrandsFormState.isOpen = false;
    BrandsFormState.type = null;

    BrandsFormState.entityType = null;
    BrandsFormState.editId = null;

    BrandsFormState.brandName = '';
    BrandsFormState.selectedPrincipal = null;
    BrandsFormState.brandItemCounts = 0;
    BrandsFormState.brandItemNames = [];

    BrandsFormState.parentName = '';
    BrandsFormState.brandList = [];

    BrandsFormState.showDeleteModal = false;

    BrandsFormState.nameError = '';

    BrandsFormState.initialBrandData = null;
    BrandsFormState.initialParentData = null;
  },

  handleChangeName: (name: string, entityType: EntityType) => {
    if (BrandsFormState.nameError) {
      BrandsFormState.nameError = '';
    }

    if (entityType === 'brand') {
      BrandsFormState.brandName = name;
    } else {
      BrandsFormState.parentName = name;
    }
  },

  handleToggleDeleteModal: () => {
    BrandsFormState.showDeleteModal = !BrandsFormState.showDeleteModal;
  },

  handleToggleDeleteAllOutletModal: () => {
    BrandsFormState.showDeleteAllOutletModal = !BrandsFormState.showDeleteAllOutletModal;
  },

  handleMutationError: (e: unknown) => {
    assertUtil.assertObjectIsTruthy<{
      code: string;
      message: string;
    }>(e);

    /* istanbul ignore next */
    if (INLINE_ERROR_CODE.includes(e.code)) {
      BrandsFormState.nameError = e.message;
    } else if (GENERAL_ERROR_CODE.includes(e.code)) {
      toast.error('A technical error has occurred. Please try again.');
    } else {
      toast.error(e.message || 'A technical error has occurred. Please try again.');
    }
  },

  createParent: async (payload: BrandsModel.CreateParentRequest, handler = parentCreatorHandler) => {
    try {
      const res = await handler(payload);

      toast.success(`${res.name} successfully created.`);
      BrandsFormState.handleCloseForm();
      BrandsState.fetchParentList(-1);
    } catch (e) {
      BrandsFormState.handleMutationError(e);
    } finally {
      BrandsFormState.isLoading = false;
    }
  },

  updateParent: async (principalId: number, payload: BrandsModel.UpdateParentRequest, handler = parentUpdatorHandler) => {
    try {
      const res = await handler(principalId, payload);

      toast.success(`${res.name} successfully modified.`);
      BrandsFormState.handleCloseForm();

      BrandsState.fetchParentList(principalId, payload.outlet_id);
    } catch (e) {
      BrandsFormState.handleMutationError(e);
    } finally {
      BrandsFormState.isLoading = false;
    }
  },

  deleteParent: async (parentId: number, outletId: number, isAllOutlet: boolean, parentName: string, handler = parentDeletionHandler) => {
    try {
      await handler(parentId, outletId, isAllOutlet);

      toast.success(`${parentName} successfully deleted.`);
      BrandsFormState.showDeleteAllOutletModal = false;
      BrandsFormState.showDeleteModal = false;
      BrandsFormState.handleCloseForm();

      BrandsState.fetchParentList(-1);
    } catch (e: unknown) {
      handleGeneralError(e);
    } finally {
      BrandsFormState.isLoading = false;
    }
  },

  createBrand: async (outletId: number, payload: BrandsModel.CreateBrandRequest, handler = brandCreatorHandler) => {
    try {
      const res = await handler(outletId, payload);

      toast.success(`${res.name} successfully created.`);
      BrandsFormState.handleCloseForm();

      BrandsState.fetchParentList(payload.principal_id as unknown as number, outletId);
    } catch (e) {
      BrandsFormState.handleMutationError(e);
    } finally {
      BrandsFormState.isLoading = false;
    }
  },

  updateBrand: async (outletId: number, brandId: number, payload: BrandsModel.UpdateBrandRequest, handler = brandUpdatorHandler) => {
    try {
      const res = await handler(outletId, brandId, payload);

      toast.success(`${res.name} successfully modified.`);
      BrandsFormState.handleCloseForm();

      BrandsState.fetchParentList(payload.principal_id as unknown as number, outletId);
    } catch (e) {
      BrandsFormState.handleMutationError(e);
    } finally {
      BrandsFormState.isLoading = false;
    }
  },

  deleteBrand: async (brandId: number, outletId: number, isAllOutlet: boolean, handler = brandDeletionHandler) => {
    try {
      await handler(brandId, outletId, isAllOutlet);

      toast.success(`${BrandsFormState.brandName} successfully deleted.`);
      BrandsFormState.showDeleteAllOutletModal = false;
      BrandsFormState.showDeleteModal = false;
      BrandsFormState.handleCloseForm();

      BrandsState.fetchParentList(-1, outletId);
    } catch (e) {
      handleGeneralError(e);
    } finally {
      BrandsFormState.isLoading = false;
    }
  },

  handleClickDelete: () => {
    const editId = BrandsFormState.editId as unknown as number;
    const outletId = BrandsState.selectedOutlet.key;
    const isAllOutlet = BrandsFormState.showDeleteAllOutletModal;

    BrandsFormState.isLoading = true;

    if (BrandsFormState.entityType === 'parent') {
      BrandsFormState.deleteParent(editId, outletId, isAllOutlet, BrandsFormState.parentName);
    } else {
      BrandsFormState.deleteBrand(editId, outletId, isAllOutlet);
    }
  },

  handleClickAssign: () => {
    if (BrandsFormState.entityType === 'brand') {
      BrandsFormState.selectedPrincipal = BrandsAssignState.selectedParent;
    } else {
      BrandsFormState.brandList = BrandsAssignState.selectedBrandList;
    }

    BrandsAssignState.searchFilter = '';
  },

  handleSubmitShortcutAssign: async () => {
    // only for brand for now

    const brandId = BrandsFormState.editId as unknown as number;
    let principalId = BrandsAssignState.selectedParent?.id as number | null;

    if (principalId === -1) {
      principalId = null;
    }

    BrandsFormState.updateBrand(BrandsState.selectedOutlet.key, brandId, {
      id: brandId,
      name: BrandsFormState.brandName,
      principal_id: principalId,
    });

    BrandsAssignState.searchFilter = '';
  },

  handleClickSave: () => {
    const editId = BrandsFormState.editId as unknown as number;
    const outletId = BrandsState.selectedOutlet.key;

    BrandsFormState.isLoading = true;

    if (BrandsFormState.entityType === 'parent') {
      if (BrandsFormState.type === 'create') {
        const payload: BrandsModel.CreateParentRequest = {
          name: BrandsFormState.parentName,
          brand_ids: BrandsFormState.brandList.map((brand) => brand.id),
          outlet_id: outletId,
          is_saved_to_all_outlets: false,
        };

        BrandsFormState.createParent(payload);
      } else {
        const payload: BrandsModel.UpdateParentRequest = {
          name: BrandsFormState.parentName,
          brand_ids: BrandsFormState.brandList.map((brand) => brand.id),
          outlet_id: outletId,
          is_saved_to_all_outlets: false,
        };

        BrandsFormState.updateParent(editId, payload);
      }
    } else if (BrandsFormState.entityType === 'brand') {
      let principalId = BrandsFormState.selectedPrincipal?.id as unknown as number | null;

      if (principalId === -1) {
        principalId = null;
      }

      if (BrandsFormState.type === 'create') {
        const payload: BrandsModel.CreateBrandRequest = {
          id: null,
          name: BrandsFormState.brandName,
          principal_id: principalId,
        };

        BrandsFormState.createBrand(outletId, payload);
      } else {
        const payload: BrandsModel.UpdateBrandRequest = {
          id: editId,
          name: BrandsFormState.brandName,
          principal_id: principalId,
        };

        BrandsFormState.updateBrand(outletId, editId, payload);
      }
    }
  },

  handleClickSaveToAllOutlet: async () => {
    const outletId = BrandsState.selectedOutlet.key;

    BrandsFormState.isLoading = true;

    if (BrandsFormState.entityType === 'parent') {
      if (BrandsFormState.type === 'create') {
        const payload: BrandsModel.CreateParentRequest = {
          name: BrandsFormState.parentName,
          brand_ids: BrandsFormState.brandList.map((brand) => brand.id),
          outlet_id: outletId,
          is_saved_to_all_outlets: true,
        };

        BrandsFormState.createParent(payload);
      } else {
        const payload: BrandsModel.UpdateParentRequest = {
          name: BrandsFormState.parentName,
          brand_ids: BrandsFormState.brandList.map((brand) => brand.id),
          outlet_id: outletId,
          is_saved_to_all_outlets: true,
        };

        BrandsFormState.updateParent(BrandsFormState.editId as unknown as number, payload);
      }
    } else if (BrandsFormState.entityType === 'brand') {
      let principalId = BrandsFormState.selectedPrincipal?.id as unknown as number | null;

      if (principalId === -1) {
        principalId = null;
      }

      try {
        const res = await inventoryApi.createBrandAllOutlet(BrandsState.selectedOutlet.key, {
          id: BrandsFormState.editId,
          name: BrandsFormState.brandName,
          principal_id: principalId,
        });

        toast.success(`${res.name} successfully ${BrandsFormState.type === 'create' ? 'created' : 'modified'}.`);

        BrandsFormState.handleCloseForm();
        BrandsState.fetchParentList(-1);
      } catch (e) {
        // TODO: handle error
        /* istanbul ignore next */
        BrandsFormState.handleMutationError(e);
      } finally {
        BrandsFormState.isLoading = false;
      }
    }
  },
});

export { BrandsFormState, type IBrandsFormState, isSaveBrandDisabled, isSaveParentDisabled };
